import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Paper,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import * as Yup from "yup";
import { useLoginMutation } from "../../api/authApi";
import { useAppDispatch } from "../../app/hooks";
import { setCredentials, consumeSessionReplacedMessage, resetSessionReplacedMessage } from "./authSlice";

const validationSchema = Yup.object({
  email: Yup.string()
    .email("Enter a valid email address")
    .required("Email is required"),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

const LoginPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [login, { isLoading }] = useLoginMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [sessionMessage, setSessionMessage] = useState<string | null>(null);

  useEffect(() => {
    const sessionReplacedMessage = consumeSessionReplacedMessage();
    if (sessionReplacedMessage) {
      setSessionMessage(sessionReplacedMessage);
    }
  }, []);

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);


  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const loginData = {
          email: values.email,
          password: values.password,
        };

        const result = await login(loginData).unwrap();
        console.log("Login result:", result);

        // Extract the actual data from ApiResponse wrapper
        const authData = result.data || result;
        dispatch(setCredentials(authData));
        resetSessionReplacedMessage();
        setSessionMessage(null);

        // Redirect based on user role
        if (authData.user && authData.user.role === "TruckDriver") {
          navigate("/truck-driver");
        } else {
          navigate("/dashboard");
        }
      } catch (err: any) {
        console.error("Login error:", err);
        if (err.status === 401) {
          setErrorMessage("Incorrect email or password");
        } else {
          setErrorMessage(
            `Login failed: ${
              err.data?.message || err.message || "Unknown error"
            }`
          );
        }
      }
    },
  });

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundImage: "url('/images/loginbackground.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: 12,
      }}
    >
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              borderRadius: 4,
            }}
          >
            <Box
              component="img"
              src="/images/adut-logo.svg"
              alt="ADUT Logo"
              sx={{
                m: 1,
                width: 120,
                height: 80,
              }}
            />
            <Typography component="h1" variant="h5" sx={{ mt: 1 }}>
              Login
            </Typography>

            {sessionMessage && (
              <Alert
                severity="info"
                sx={{ mt: 2, width: "100%" }}
                onClose={() => setSessionMessage(null)}
              >
                {sessionMessage}
              </Alert>
            )}

            {errorMessage && (
              <Alert severity="error" sx={{ mt: 2, width: "100%" }}>
                {errorMessage}
              </Alert>
            )}

            {successMessage && (
              <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
                {successMessage}
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={formik.handleSubmit}
              sx={{ mt: 3, width: "100%" }}
            >
              <TextField
                margin="normal"
                fullWidth
                id="email"
                name="email"
                label="Email Address"
                type="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                autoComplete="email"
                autoFocus
              />
              <TextField
                margin="normal"
                fullWidth
                id="password"
                name="password"
                label="Password"
                type={showPassword ? "text" : "password"}
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.password && Boolean(formik.errors.password)
                }
                helperText={formik.touched.password && formik.errors.password}
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{ mt: 3, mb: 2 }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Login"
                )}
              </Button>
            </Box>

            <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              sx={{ mt: 2, width: "100%" }}
            >
              <Grid item>
                <Link
                  to="/forgot-password"
                  style={{
                    textDecoration: "none",
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "primary.main",
                      cursor: "pointer",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Forgot Password?
                  </Typography>
                </Link>
              </Grid>
              <Grid item>
                <Link
                  to="/signup"
                  style={{
                    textDecoration: "none",
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "primary.main",
                      cursor: "pointer",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Don't have an account? Sign up
                  </Typography>
                </Link>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default LoginPage;
